import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import { Building2, ChevronLeft, FileText, Shield, Scale, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const LegalPages = () => {
  const { page } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [useDefault, setUseDefault] = useState(true);
  
  const tabs = [
    { id: 'cgv', label: 'Conditions Générales de Vente', icon: FileText },
    { id: 'mentions-legales', label: 'Mentions Légales', icon: Scale },
    { id: 'confidentialite', label: 'Politique de Confidentialité', icon: Shield },
  ];

  const activeTab = page || 'cgv';

  useEffect(() => {
    loadContent(activeTab);
  }, [activeTab]);

  const loadContent = async (pageType) => {
    setLoading(true);
    try {
      const response = await api.getLegalPage(pageType);
      if (response.data.use_default) {
        setContent('');
        setUseDefault(true);
      } else {
        setContent(response.data.content || '');
        setUseDefault(false);
      }
    } catch (error) {
      console.error('Error loading legal page:', error);
      setContent('');
      setUseDefault(true);
    } finally {
      setLoading(false);
    }
  };

  // Render markdown content to HTML
  const renderMarkdown = (text) => {
    if (!text) return '';
    
    let html = text
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headers
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      // Bold and Italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/_(.+?)_/g, '<em>$1</em>')
      // Links
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-[#1E3A8A] hover:underline">$1</a>')
      // Unordered lists
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      // Ordered lists
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
      // Blockquotes
      .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
      // Horizontal rules
      .replace(/^---$/gm, '<hr />')
      // Paragraphs
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />');
    
    // Wrap lists
    html = html.replace(/(<li>.*<\/li>)+/g, '<ul>$&</ul>');
    
    return `<p>${html}</p>`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#1E3A8A] rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900">CoproEnergy</span>
            </Link>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(`/legal/${tab.id}`)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-[#1E3A8A] text-[#1E3A8A]'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <article className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 prose prose-slate max-w-none">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
            </div>
          ) : useDefault ? (
            // Render default static content
            <>
              {activeTab === 'cgv' && <CGVContent />}
              {activeTab === 'mentions-legales' && <MentionsLegalesContent />}
              {activeTab === 'confidentialite' && <ConfidentialiteContent />}
            </>
          ) : (
            // Render custom markdown content (sanitized for XSS protection)
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderMarkdown(content)) }} />
          )}
        </article>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <p>
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/legal/cgv" className="hover:text-[#1E3A8A]">CGV</Link>
            <span>•</span>
            <Link to="/legal/mentions-legales" className="hover:text-[#1E3A8A]">Mentions Légales</Link>
            <span>•</span>
            <Link to="/legal/confidentialite" className="hover:text-[#1E3A8A]">Confidentialité</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

// ==================== CGV ====================
const CGVContent = () => (
  <>
    <h1>Conditions Générales de Vente</h1>
    <p className="lead">
      Les présentes Conditions Générales de Vente (ci-après « CGV ») régissent les relations contractuelles entre 
      CoproEnergy et ses clients professionnels dans le cadre de l'utilisation de la plateforme SaaS CoproEnergy.
    </p>

    <h2>Article 1 – Objet</h2>
    <p>
      Les présentes CGV ont pour objet de définir les conditions dans lesquelles CoproEnergy fournit à ses clients 
      professionnels (ci-après « le Client ») un accès à sa plateforme de gestion commerciale dédiée à la rénovation 
      énergétique des copropriétés (ci-après « le Service »).
    </p>
    <p>
      Le Service comprend notamment :
    </p>
    <ul>
      <li>L'accès à la plateforme web CoproEnergy</li>
      <li>L'import et la gestion des données du Registre National des Copropriétés (RNIC)</li>
      <li>L'enrichissement des données via l'API ADEME (DPE)</li>
      <li>Les outils de prospection et de suivi commercial</li>
      <li>Le calcul des primes CEE potentielles</li>
      <li>Le support technique par système de tickets</li>
    </ul>

    <h2>Article 2 – Acceptation des CGV</h2>
    <p>
      L'inscription au Service implique l'acceptation pleine et entière des présentes CGV. Le Client reconnaît 
      avoir pris connaissance des CGV préalablement à son inscription et les accepter sans réserve.
    </p>

    <h2>Article 3 – Abonnement et Tarification</h2>
    <h3>3.1 Formules d'abonnement</h3>
    <p>CoproEnergy propose deux formules d'abonnement :</p>
    <ul>
      <li><strong>Formule Mensuelle :</strong> 99€ HT par licence et par mois, sans engagement de durée</li>
      <li><strong>Formule Annuelle :</strong> 79€ HT par licence et par mois, avec engagement de 12 mois (soit 948€ HT par an et par licence)</li>
    </ul>
    <p>Une licence correspond à un utilisateur nominatif de la plateforme.</p>

    <h3>3.2 Paiement</h3>
    <p>
      Le paiement s'effectue par carte bancaire via la plateforme sécurisée Stripe. Pour la formule mensuelle, 
      le prélèvement est effectué chaque mois à date anniversaire. Pour la formule annuelle, le paiement de 
      l'intégralité de la période est effectué à la souscription.
    </p>

    <h3>3.3 TVA</h3>
    <p>
      Les prix indiqués sont exprimés hors taxes (HT). La TVA applicable (20%) sera ajoutée au montant HT.
    </p>

    <h2>Article 4 – Durée et Résiliation</h2>
    <h3>4.1 Durée</h3>
    <p>
      <strong>Formule Mensuelle :</strong> L'abonnement est conclu pour une durée d'un mois, renouvelable tacitement.
    </p>
    <p>
      <strong>Formule Annuelle :</strong> L'abonnement est conclu pour une durée de 12 mois, renouvelable tacitement.
    </p>

    <h3>4.2 Résiliation</h3>
    <p>
      <strong>Formule Mensuelle :</strong> Le Client peut résilier son abonnement à tout moment, avec effet à la fin 
      du mois en cours. La résiliation doit être effectuée depuis l'espace client ou par email à support@coproenergy.fr.
    </p>
    <p>
      <strong>Formule Annuelle :</strong> Le Client peut résilier son abonnement au moins 30 jours avant la date 
      anniversaire. À défaut, l'abonnement sera renouvelé pour une nouvelle période de 12 mois.
    </p>

    <h2>Article 5 – Support</h2>
    <p>
      Le support technique est accessible via le système de tickets intégré à la plateforme. Les délais 
      de réponse cibles sont :
    </p>
    <ul>
      <li><strong>Priorité Urgente :</strong> 4 heures ouvrées</li>
      <li><strong>Priorité Haute :</strong> 24 heures ouvrées</li>
      <li><strong>Priorité Normale :</strong> 48 heures ouvrées</li>
      <li><strong>Priorité Basse :</strong> 72 heures ouvrées</li>
    </ul>

    <h2>Article 6 – Droit Applicable</h2>
    <p>
      Les présentes CGV sont soumises au droit français. En cas de litige, les parties s'engagent à 
      rechercher une solution amiable. À défaut, les tribunaux compétents de Paris seront seuls compétents.
    </p>

    <h2>Article 7 – Contact</h2>
    <p>
      Pour toute question relative aux présentes CGV, le Client peut contacter CoproEnergy :
    </p>
    <ul>
      <li>Par email : contact@coproenergy.fr</li>
      <li>Via le système de tickets de la plateforme</li>
    </ul>
  </>
);

// ==================== MENTIONS LÉGALES ====================
const MentionsLegalesContent = () => (
  <>
    <h1>Mentions Légales</h1>
    <p className="lead">
      Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie 
      numérique, il est porté à la connaissance des utilisateurs du site CoproEnergy les présentes mentions légales.
    </p>

    <h2>1. Éditeur du site</h2>
    <p>
      Le site web CoproEnergy est édité par :
    </p>
    <ul>
      <li><strong>Raison sociale :</strong> CoproEnergy SAS</li>
      <li><strong>Siège social :</strong> [Adresse à compléter], France</li>
      <li><strong>Capital social :</strong> [À compléter] €</li>
      <li><strong>RCS :</strong> [Numéro RCS à compléter]</li>
      <li><strong>SIRET :</strong> [Numéro SIRET à compléter]</li>
      <li><strong>TVA intracommunautaire :</strong> [À compléter]</li>
      <li><strong>Email :</strong> contact@coproenergy.fr</li>
    </ul>

    <h3>Directeur de la publication</h3>
    <p>
      [Nom du directeur de publication à compléter], en qualité de [fonction].
    </p>

    <h2>2. Hébergement</h2>
    <p>
      Le site CoproEnergy est hébergé par :
    </p>
    <ul>
      <li><strong>Hébergeur :</strong> [Nom de l'hébergeur]</li>
      <li><strong>Adresse :</strong> [Adresse de l'hébergeur]</li>
    </ul>

    <h2>3. Propriété Intellectuelle</h2>
    <p>
      L'ensemble du contenu du site CoproEnergy est protégé par le droit de la propriété intellectuelle.
      La marque CoproEnergy et son logo sont des marques déposées.
    </p>

    <h2>4. Données Personnelles</h2>
    <p>
      CoproEnergy collecte et traite des données personnelles conformément au RGPD.
      Pour plus d'informations, consultez notre <Link to="/legal/confidentialite" className="text-[#1E3A8A]">Politique de Confidentialité</Link>.
    </p>

    <h3>Délégué à la Protection des Données (DPO)</h3>
    <p>
      Contact : dpo@coproenergy.fr
    </p>

    <h2>5. Contact</h2>
    <p>
      Pour toute question : contact@coproenergy.fr
    </p>
  </>
);

// ==================== POLITIQUE DE CONFIDENTIALITÉ ====================
const ConfidentialiteContent = () => (
  <>
    <h1>Politique de Confidentialité</h1>
    <p className="lead">
      CoproEnergy accorde une grande importance à la protection de vos données personnelles. La présente 
      politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et protégeons 
      vos données conformément au Règlement Général sur la Protection des Données (RGPD).
    </p>

    <h2>1. Responsable du Traitement</h2>
    <ul>
      <li><strong>CoproEnergy SAS</strong></li>
      <li>Email : contact@coproenergy.fr</li>
    </ul>

    <h2>2. Données Collectées</h2>

    <h3>2.1 Données d'identification</h3>
    <ul>
      <li>Nom et prénom</li>
      <li>Adresse email professionnelle</li>
      <li>Numéro de téléphone professionnel</li>
      <li>Raison sociale de l'entreprise</li>
      <li>Numéro SIRET</li>
    </ul>

    <h3>2.2 Données de connexion</h3>
    <ul>
      <li>Identifiants de connexion</li>
      <li>Adresse IP</li>
      <li>Logs de connexion et d'activité</li>
    </ul>

    <h3>2.3 Données de facturation</h3>
    <ul>
      <li>Informations de paiement (gérées par Stripe)</li>
      <li>Historique des transactions</li>
    </ul>

    <h2>3. Finalités du Traitement</h2>
    <ul>
      <li><strong>Gestion du compte :</strong> création et gestion de votre compte</li>
      <li><strong>Fourniture du service :</strong> accès à la plateforme</li>
      <li><strong>Facturation :</strong> gestion des abonnements</li>
      <li><strong>Support :</strong> traitement des demandes d'assistance</li>
      <li><strong>Amélioration :</strong> analyse d'usage pour améliorer la plateforme</li>
    </ul>

    <h2>4. Durée de Conservation</h2>
    <ul>
      <li><strong>Données de compte :</strong> durée de l'abonnement + 3 ans</li>
      <li><strong>Données de facturation :</strong> 10 ans</li>
      <li><strong>Logs de connexion :</strong> 1 an</li>
    </ul>

    <h2>5. Vos Droits</h2>
    <p>
      Conformément au RGPD, vous disposez des droits suivants :
    </p>
    <ul>
      <li><strong>Droit d'accès</strong></li>
      <li><strong>Droit de rectification</strong></li>
      <li><strong>Droit à l'effacement</strong></li>
      <li><strong>Droit à la portabilité</strong></li>
      <li><strong>Droit d'opposition</strong></li>
    </ul>
    <p>
      Pour exercer ces droits : <strong>dpo@coproenergy.fr</strong>
    </p>

    <h2>6. Sécurité des Données</h2>
    <ul>
      <li>Chiffrement HTTPS/TLS</li>
      <li>Mots de passe chiffrés (bcrypt)</li>
      <li>Authentification sécurisée (JWT)</li>
      <li>Accès restreint selon les rôles</li>
    </ul>

    <h2>7. Contact</h2>
    <p>
      Pour toute question : <strong>dpo@coproenergy.fr</strong>
    </p>
  </>
);

export default LegalPages;
